import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Link,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/httpClient';
import PublicNavBar from './PublicNavBar';
import { useAuth } from '../context/AuthContext';
import { authPath, persistRedirectFromSearch, postLoginPath } from '../utils/authRedirect';

const TERMS_VERSION = '2026-04-01-v3';
const TERMS_OF_SERVICE = [
  {
    title: 'Acceptance of Terms',
    body: 'By creating an account, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and all applicable laws and regulations.',
  },
  {
    title: 'User Responsibility for Uploaded Content',
    body: 'You are solely responsible for all content you upload, transmit, store, or process through the service. You represent and warrant that you have all legal rights and permissions required for such content.',
  },
  {
    title: 'Strictly Prohibited Content',
    body: 'You must not upload, process, or distribute any illegal content, including but not limited to child sexual abuse material, exploitative content involving minors, copyrighted material without authorization, trade secret theft, malware, or content that violates privacy, publicity, or other third-party rights.',
  },
  {
    title: 'Regulated and Sensitive Data',
    body: 'You are solely responsible for ensuring uploads comply with all applicable laws and sector rules, including HIPAA, GLBA, FERPA, PCI DSS, GDPR, and applicable state privacy laws. Do not upload regulated data unless you have independently ensured your own legal compliance for your use case.',
  },
  {
    title: 'Data Security and Data Loss Disclaimer',
    body: 'To the maximum extent permitted by law, the service is provided on an "as is" and "as available" basis. We do not guarantee uninterrupted availability, absolute security, or error-free operation and are not liable for unauthorized access, cybersecurity incidents, hacking, data leaks, corruption, or data loss.',
  },
  {
    title: 'Indemnification and Enforcement',
    body: 'You agree to defend, indemnify, and hold harmless us and our affiliates from claims arising from your content, your use of the service, or your violation of law. We may remove content, suspend or terminate accounts, preserve evidence, and cooperate with law enforcement.',
  },
];

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    mobilePhone: '',
    inviteCode: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationConfig, setRegistrationConfig] = useState({
    inviteCodeRequired: true,
    openRegistration: false
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const loginHref = authPath('/login', location.search);

  useEffect(() => {
    persistRedirectFromSearch(location.search);
  }, [location.search]);

  useEffect(() => {
    const fetchRegistrationConfig = async () => {
      try {
        const response = await api.get('/v1/auth/registration-info');
        setRegistrationConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch registration config:', error);
        // Keep default values if fetch fails
      } finally {
        setConfigLoading(false);
      }
    };

    fetchRegistrationConfig();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setTermsError('You must accept the Terms of Service to create an account.');
      setLoading(false);
      return;
    }

    // Client-side validation for better UX
    const errors = {};
    
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      setLoading(false);
      return;
    }

    // Validate mobile phone - match backend pattern: ^\+?[0-9\s\-\(\)]+$
    const phonePattern = /^\+?[0-9\s\-\(\)]+$/;
    if (!formData.mobilePhone || !phonePattern.test(formData.mobilePhone)) {
      errors.mobilePhone = 'Phone number can only contain digits, spaces, hyphens, and parentheses';
      setFieldErrors(errors);
      setError('Please fix the validation errors');
      setLoading(false);
      return;
    }

    // Only validate invite code if required by backend configuration
    if (registrationConfig.inviteCodeRequired && (!formData.inviteCode || formData.inviteCode.trim().length === 0)) {
      setError('Invite code is required');
      setLoading(false);
      return;
    }
    
    setFieldErrors({});

    try {
      const registrationData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobilePhone: formData.mobilePhone,
        termsAccepted: acceptedTerms,
        termsVersion: TERMS_VERSION,
      };

      // Only include invite code if required by backend configuration
      if (registrationConfig.inviteCodeRequired) {
        registrationData.inviteCode = formData.inviteCode;
      }

      await api.post('/v1/auth/register', registrationData);

      const signedIn = await login({
        username: formData.username,
        password: formData.password,
      });
      if (signedIn.success) {
        navigate(postLoginPath(location.search), { replace: true });
        return;
      }

      setSuccess('Account created. Please sign in to claim your gateway.');
      setTimeout(() => {
        navigate(loginHref);
      }, 1500);
    } catch (error) {
      console.error('Registration error:', error.response); // Debug log
      
      // Handle different types of errors
      let errorMessage = 'Registration failed';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle validation errors (400 Bad Request with field errors)
        if (error.response.status === 400) {
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors && Array.isArray(errorData.errors)) {
            // Handle field validation errors
            errorMessage = errorData.errors.map(err => err.defaultMessage || err.message).join(', ');
          } else if (errorData.fieldErrors) {
            // Alternative validation error format
            errorMessage = Object.values(errorData.fieldErrors).flat().join(', ');
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } else {
          // Handle other error types
          errorMessage = errorData.message || errorData.error || errorMessage;
        }
      }
      
      setError(errorMessage);
    }
    
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavBar />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom>
              Create Account
            </Typography>
            
            {configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
              Join EyeSense.ai Hub
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ✓ Have you installed your Gateway Software?
              </Typography>
              <Typography variant="caption">
                You'll need to have deployed at least one EyeSense.ai Gateway before you can manage it via this hub interface. If you haven't installed your gateway yet, please{' '}
                <Link
                  component="button"
                  variant="inherit"
                  onClick={() => navigate('/install-gateway')}
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                  follow our installation guide
                </Link>
                {' '}after creating your account.
              </Typography>
            </Alert>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              {registrationConfig.inviteCodeRequired && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="inviteCode"
                  label="Invite Code"
                  id="inviteCode"
                  autoComplete="off"
                  value={formData.inviteCode}
                  onChange={handleChange}
                  helperText="Enter your invite code to create an account"
                  sx={{ mb: 2 }}
                />
              )}
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </Box>
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                value={formData.username}
                onChange={handleChange}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="mobilePhone"
                label="Mobile Phone"
                name="mobilePhone"
                type="tel"
                autoComplete="tel"
                value={formData.mobilePhone}
                onChange={handleChange}
                error={!!fieldErrors.mobilePhone}
                helperText={fieldErrors.mobilePhone || "Format: +1 (123) 456-7890 or 123-456-7890"}
                onBlur={(e) => {
                  // Validate on blur for immediate feedback
                  const phonePattern = /^\+?[0-9\s\-\(\)]+$/;
                  if (e.target.value && !phonePattern.test(e.target.value)) {
                    setFieldErrors({
                      ...fieldErrors,
                      mobilePhone: 'Phone number can only contain digits, spaces, hyphens, and parentheses'
                    });
                  } else {
                    const newErrors = { ...fieldErrors };
                    delete newErrors.mobilePhone;
                    setFieldErrors(newErrors);
                  }
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
              />

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  maxHeight: 260,
                  overflowY: 'auto',
                  backgroundColor: 'background.default',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Terms of Service (Version {TERMS_VERSION})
                </Typography>
                {TERMS_OF_SERVICE.map((section) => (
                  <Box key={section.title} sx={{ mb: 1.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {section.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {section.body}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <FormControl required error={!!termsError} sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={(event) => {
                        setAcceptedTerms(event.target.checked);
                        if (termsError) {
                          setTermsError('');
                        }
                      }}
                      name="acceptedTerms"
                    />
                  }
                  label="I have read and agree to the Terms of Service."
                />
                {termsError && <FormHelperText>{termsError}</FormHelperText>}
              </FormControl>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !acceptedTerms}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>
              
              <Box textAlign="center" mt={2}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate(loginHref)}
                  sx={{ textDecoration: 'none' }}
                >
                  Already have an account? Sign in
                </Link>
              </Box>
            </Box>
              </>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
